import { DateTime } from 'luxon';
import kfetch from '@kareo/kfetch';
import rollbar from 'shared-assets/src/errors/errors';
import getSessionId from '../utils/sessionId';

const ADMIN_UI_BASE = '/admin-ui/api';
const COMMON_UI_BASE = '/common-ui/api';
const DASHBOARD_CALENDAR_UI_BASE = '/dashboard-calendar-ui/api';

class Practice {
  constructor() {
    this.instance = null;
    this.practice = null;
    this.adminPractice = null;
  }

  static get() {
    if (!this.instance) {
      this.instance = new Practice();
    }

    return this.instance;
  }

  /**
   * CP-6432 Override default `no-cache` setting to allow browser to use response Cache-Control value
   * @function getCacheConfig: Gets config with relevant cache header values
   * @returns cacheConfig object
   */
  getCacheConfig() {
    return {
      headers: {
        'Cache-Control': 'max-age=60',
        Pragma: 'max-age=60',
      },
    };
  }

  /**
   * @function practiceInfo: Gets the practice data (from common-ui by default)
   * Utilizes caching to avoid unnecessary/excess calls to the API.
   * @param {object} config: Request configurations
   * @param {boolean} isAdminUrl: If true, uses admin-ui as the base url to get the practice
   * @returns practice data, either from common-ui or admin-ui
   */
  async practiceInfo(config = {}, isAdminUrl = false) {
    const sessionId = getSessionId();
    const baseUrl = isAdminUrl ? ADMIN_UI_BASE : COMMON_UI_BASE;
    const apiUrl = `${baseUrl}/Practice?sessionId=${encodeURIComponent(
      sessionId,
    )}`;

    const practice = isAdminUrl ? 'adminPractice' : 'practice';

    // If not application-cached yet, fetch and initialize the practice data
    if (!this[practice]) {
      try {
        // Fetch the data from the server
        const response = await kfetch(apiUrl, config);
        // Save the response to application cache
        this[practice] = response || null;
      } catch (error) {
        rollbar.error(error);
      }
    }
    return this[practice];
  }

  /**
   * @function adminPracticeInfo: Gets the practice data from admin-ui
   * Utilizes caching to avoid unnecessary/excess calls to the API.
   * @param {object} config: Request configurations
   * @returns practice data from admin-ui
   */
  async adminPracticeInfo(config = {}) {
    // If not cached yet, fetch and initialize the admin practice data
    if (!this.adminPractice) {
      this.adminPractice = await practiceInfo(config, true);
    }
    // Otherwise reuse the cached data
    return this.adminPractice;
  }

  /**
   * @function load: Loads the practice data (from common-ui by default).
   * Utilizes caching to avoid unnecessary/excess calls to the API.
   * @param {boolean} isAdminUrl: If true, uses admin-ui as the base url to get the practice
   * @returns practice data, either from common-ui or admin-ui
   */
  async load(isAdminUrl = false) {
    // If not cached yet, fetch and initialize the practice data
    if (isAdminUrl && !this.adminPractice) {
      this.adminPractice = await practiceInfo({}, isAdminUrl);
    } else if (!this.practice) {
      this.practice = await practiceInfo({});
    }

    // Otherwise reuse the cached data
    try {
      return isAdminUrl ? await this.practice : await this.adminPractice;
    } catch (e) {
      // allow retry on failure
      if (isAdminUrl) this.adminPractice = null;
      else this.practice = null;

      return {};
    }
  }

  /**
   * @function getSchedules
   * @param {number} startDateTime - Start Date Time in Mills.
   * @param {number} endDateTime - End Date Time in Mills.
   * @param {object} config - kfetch config object
   * @param {AbortSignal} config.signal - from new AbortController.signal
   * @returns {Promise<any>}
   */
  getSchedules = async (
    startDateTime = DateTime.local().startOf('day').toMillis(),
    endDateTime = DateTime.local().endOf('day').toMillis(),
    config,
  ) => {
    const response = await kfetch(
      `${DASHBOARD_CALENDAR_UI_BASE}/schedules?&startDateTime=${startDateTime}&endDateTime=${endDateTime}`,
      config,
    );

    return response;
  };

  async getTimeZone(config = {}) {
    let url = `${COMMON_UI_BASE}/Practice/timezone`;
    const response = await kfetch(url, config);
    return response;
  }
}

// Export a single instance of the Practice class
const practice = Practice.get();

export default practice;
